/**
 * 案件の読取 5 本（`listPjs` / `getChangeHistory` / `listPjRequirements` / `getPjDiff` /
 * `getPjCharter`）の TanStack Query ラッパー。
 *
 * 画面が `rpcClient` を直接叩かないための層である。Query キー（`query-keys.ts`）と入力の
 * 対応をここだけで決める。画面側で `useQuery` を直接書くと、同じ procedure に別のキーが
 * 付いた瞬間にキャッシュが分裂する。
 *
 * **案件番号は number である**（決定 2）。
 *
 * `usePjLaneChanges` は `getChangeHistory` の応答のうち、配下から伝播したレーン 4 型の変更
 * （`laneChanges`）だけを、`usePjScreenChanges` は持ち主に寄せた画面の変更（`screenChanges`）
 * だけを選ぶ。procedure もキーも `usePjChanges` と同じである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の一覧・変更の行・要求・欄ごとの差分・憲章を TanStack Query のフックとして画面へ供給する。
 * @graph-connects rpcClient [calls] specGraph.listPjs / getChangeHistory / listPjRequirements / getPjDiff / getPjCharter
 */
import type {
  GetChangeHistoryOutput,
  GetPjCharterOutput,
  GetPjDiffOutput,
  ListPjRequirementsOutput,
  ListPjsOutput,
} from '@cortex/domain-spec-console';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { useSpecLocale } from '../../i18n/spec-locale.js';
import { rpcClient } from '../../lib/orpc.js';
import { queryKeys } from '../../lib/query-keys.js';

/**
 * 案件を一覧する。変更の行数と要求の件数が付く（決定 16）。
 *
 * @returns 案件の一覧
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の一覧を取得する。
 * @graph-connects rpcClient [calls] specGraph.listPjs
 */
export function usePjs(): UseQueryResult<ListPjsOutput> {
  return useQuery({
    queryKey: queryKeys.specGraph.listPjs(),
    queryFn: () => rpcClient.specGraph.listPjs({}),
  });
}

/**
 * 案件の変更の行を取得する。**案件で引くので、返るのは案件の変更（`changes`）である。**
 *
 * 操作履歴（`operations`）は同じ応答に空のページとして載るが、この経路では使わない。差分の
 * 印の出どころは案件の変更の 1 本に絞るという決定（変更履歴 §3.3）に、呼び出しの形で従う。
 *
 * @param pjNumber 案件番号
 * @returns その案件の変更の行と、空の操作履歴のページ
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の変更の行を、案件番号で引いて取得する。
 * @graph-connects rpcClient [calls] specGraph.getChangeHistory
 */
export function usePjChanges(pjNumber: number): UseQueryResult<GetChangeHistoryOutput> {
  return useQuery({
    queryKey: queryKeys.specGraph.getChangeHistory(pjNumber),
    queryFn: () => rpcClient.specGraph.getChangeHistory({ pjNumber }),
  });
}

/**
 * 案件が持つ要求を、why・根拠・結ばれたノードの id と一緒に取得する。
 *
 * @param pjNumber 案件番号
 * @returns その案件の要求
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が持つ要求を取得する。
 * @graph-connects rpcClient [calls] specGraph.listPjRequirements
 */
export function usePjRequirements(pjNumber: number): UseQueryResult<ListPjRequirementsOutput> {
  return useQuery({
    queryKey: queryKeys.specGraph.listPjRequirements(pjNumber),
    queryFn: () => rpcClient.specGraph.listPjRequirements({ pjNumber }),
  });
}

/**
 * 案件で変わったレーン 4 型（ステージ・業務フロー・業務・業務ルール）を、配下の変更を含めて取得する。
 *
 * **`usePjChanges` と同じ Query キーで同じ procedure を引く。** 伝播は案件の変更の行と
 * 同じ応答（`laneChanges`）に載っているので、キーを分けると同じ読取を 2 回撃つ。ルートが変更の
 * 行を揃えてから画面を描くので、画面がこれを呼んだ時点でキャッシュに答えが在る。
 *
 * @param pjNumber 案件番号
 * @returns 直接と配下の変わったノード（id の昇順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わったステージ・業務フロー・業務・業務ルールを、配下からの伝播を含めて取得する。
 * @graph-connects rpcClient [calls] specGraph.getChangeHistory
 */
export function usePjLaneChanges(
  pjNumber: number,
): UseQueryResult<GetChangeHistoryOutput['laneChanges']> {
  return useQuery({
    queryKey: queryKeys.specGraph.getChangeHistory(pjNumber),
    queryFn: () => rpcClient.specGraph.getChangeHistory({ pjNumber }),
    select: (data) => data.laneChanges,
  });
}

/**
 * 案件で変わった画面（部品の変更は持ち主の画面に寄せたもの）を、部品が操作する業務・属する
 * ステージと一緒に取得する。
 *
 * **`usePjChanges` と同じ Query キーで同じ procedure を引く**（`usePjLaneChanges` と同じ理由）。
 * 部品を画面へ寄せる判定は API 側の 1 関数が持ち、画面は寄せ直さない。
 *
 * @param pjNumber 案件番号
 * @returns 案件で変わった画面（id の昇順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件で変わった画面を、部品が操作する業務・属するステージと一緒に取得する。
 * @graph-connects rpcClient [calls] specGraph.getChangeHistory
 */
export function usePjScreenChanges(
  pjNumber: number,
): UseQueryResult<GetChangeHistoryOutput['screenChanges']> {
  return useQuery({
    queryKey: queryKeys.specGraph.getChangeHistory(pjNumber),
    queryFn: () => rpcClient.specGraph.getChangeHistory({ pjNumber }),
    select: (data) => data.screenChanges,
  });
}

/**
 * 案件の差分（要素ごとの欄の変更前・変更後）を取得する。
 *
 * **欄の突き合わせは API 側の 1 関数が持つ**（MCP の `get_spec_changes` と同じ関数）。画面で
 * 行の `before` / `after` を展開し直すと、画面と MCP で「変わった欄」が別々に決まる。
 *
 * @param pjNumber 案件番号
 * @returns その案件の差分
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が変えた要素ごとの欄の変更前・変更後を取得する。
 * @graph-connects rpcClient [calls] specGraph.getPjDiff
 */
export function usePjDiff(pjNumber: number): UseQueryResult<GetPjDiffOutput> {
  const locale = useSpecLocale();
  return useQuery({
    queryKey: queryKeys.specGraph.getPjDiff(pjNumber, locale),
    queryFn: () =>
      rpcClient.specGraph.getPjDiff(locale === undefined ? { pjNumber } : { pjNumber, locale }),
  });
}

/**
 * 案件の憲章（値のある欄を目的から備考の順に）を取得する。保存されていなければ `charter` が null。
 *
 * **値があるかと欄の並びは API が決める**（project-management の MCP `get_charter` と同じ読みの正
 * `getCharter` を通す）。画面は受け取った欄を並べるだけで、空の欄を落とし直さない。
 *
 * @param pjNumber 案件番号
 * @returns その案件の憲章
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件の憲章を、値のある欄だけ目的から備考の順に取得する。
 * @graph-connects rpcClient [calls] specGraph.getPjCharter
 */
export function usePjCharter(pjNumber: number): UseQueryResult<GetPjCharterOutput> {
  return useQuery({
    queryKey: queryKeys.specGraph.getPjCharter(pjNumber),
    queryFn: () => rpcClient.specGraph.getPjCharter({ pjNumber }),
  });
}
